<template>
  <div class="transfer-amount-page">
    <h2 class="page-title mb-4">Atur Jumlah Transfer QR</h2>

    <div class="setting-card">
      <!-- Card Header -->
      <div class="card-header-section">
        <h3 class="card-main-title">Opsi Dropdown Jumlah QR</h3>
        <p class="card-description">
          Atur pilihan jumlah QR di dropdown Jumlah QR saat Dealer mengirim QR ke Pos.
        </p>
      </div>

      <!-- Card Body Content: Form Left & Preview Right -->
      <div class="card-content-section d-flex justify-content-between align-items-start">
        <!-- Left: Form Controls -->
        <div class="form-column flex-grow-1 mr-4">
          <span class="field-label-desc">Nama field</span>
          <h4 class="field-name-title">Jumlah QR</h4>

          <!-- Dynamic Option Rows -->
          <div class="options-list">
            <div
              v-for="(item, index) in options"
              :key="index"
              class="option-row d-flex align-items-center mb-3"
            >
              <span class="row-index-number">{{ index + 1 }}.</span>
              <div class="input-suffix-wrapper flex-grow-1">
                <input
                  ref="optionInputs"
                  type="text"
                  inputmode="numeric"
                  :value="item.value"
                  :disabled="isUserSE"
                  class="form-control custom-qty-input"
                  placeholder="Masukkan jumlah QR"
                  @input="handleOptionInput(index, $event)"
                />
                <span class="input-suffix-text">QR</span>
              </div>
              <button
                v-if="!isUserSE"
                type="button"
                class="btn btn-delete-row"
                @click="removeOption(index)"
                :title="'Hapus opsi ' + (index + 1)"
              >
                <i class="fas fa-times"></i>
              </button>
            </div>
          </div>

          <!-- Add Option Button -->
          <button
            v-if="!isUserSE"
            type="button"
            class="btn btn-add-option d-inline-flex align-items-center"
            @click="addOption"
          >
            <i class="fas fa-plus mr-2"></i> Tambah opsi
          </button>
        </div>

        <!-- Right: Interactive Preview Mockup -->
        <div class="preview-column">
          <div class="preview-card-box">
            <div class="preview-header-label">
              Preview di form Kirim QR (Dealer &rarr; Pos)
            </div>
            <div class="preview-field-title">
              Jumlah QR
            </div>

            <!-- Simulated Select Box -->
            <div class="preview-select-trigger" @click="isPreviewOpen = !isPreviewOpen">
              <span class="preview-select-placeholder">Pilih Jumlah QR</span>
              <i class="fas fa-caret-up preview-caret-icon" v-if="isPreviewOpen"></i>
              <i class="fas fa-caret-down preview-caret-icon" v-else></i>
            </div>

            <!-- Simulated Dropdown Menu -->
            <div class="preview-dropdown-menu" v-if="isPreviewOpen">
              <template v-if="validOptions.length">
                <div
                  v-for="(opt, idx) in validOptions"
                  :key="idx"
                  class="preview-dropdown-item"
                >
                  {{ opt.value }}
                </div>
              </template>
              <div v-else class="preview-dropdown-empty text-muted">
                Tidak ada opsi
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Card Bottom Actions -->
     <div v-if="!isUserSE" class="card-actions-section d-flex justify-content-end align-items-center" >
        <button
          type="button"
          class="btn btn-action-batal mr-3"
          @click="handleCancel"
        >
          Batal
        </button>
        <button
          type="button"
          class="btn btn-action-simpan"
          :class="{ 'btn-action-simpan--active': isChanged && !isSaving }"
          :disabled="!isChanged || isSaving"
          @click="handleSave"
        >
          <span v-if="isSaving">Menyimpan...</span>
          <span v-else>Simpan</span>
        </button>
      </div>
    </div>

    <!-- Success Toast Notification (Top Right) -->
    <transition name="toast-slide">
      <div v-if="showSuccessToast" class="top-right-toast">
        <div class="toast-icon-wrapper">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="12" fill="#22C55E"/>
            <path d="M7 12.5L10.2 15.7L17 8.5" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <div class="toast-content-wrapper">
          <h6 class="toast-title mb-0">Berhasil disimpan</h6>
          <p class="toast-desc mb-0">Opsi jumlah transfer QR berhasil diperbarui.</p>
        </div>
        <button
          type="button"
          class="btn toast-close-btn"
          @click="showSuccessToast = false"
          aria-label="Tutup"
        >
          <svg width="12" height="12" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M1 1L13 13M1 13L13 1" stroke="#9CA3AF" stroke-width="1.8" stroke-linecap="round"/>
          </svg>
        </button>
      </div>
    </transition>

    <!-- Delete Toast Notification (Top Right) -->
    <transition name="toast-slide">
      <div v-if="showDeleteToast" class="top-right-toast">
        <div class="toast-icon-wrapper">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="12" fill="#22C55E"/>
            <path d="M7 12.5L10.2 15.7L17 8.5" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <div class="toast-content-wrapper">
          <h6 class="toast-title mb-0">Opsi berhasil dihapus</h6>
          <p class="toast-desc mb-0">Opsi {{ deletedOptionValue ? deletedOptionValue + ' ' : '' }}QR dihapus dari dropdown Jumlah QR.</p>
        </div>
        <button
          type="button"
          class="btn toast-close-btn"
          @click="showDeleteToast = false"
          aria-label="Tutup"
        >
          <svg width="12" height="12" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M1 1L13 13M1 13L13 1" stroke="#9CA3AF" stroke-width="1.8" stroke-linecap="round"/>
          </svg>
        </button>
      </div>
    </transition>

    <!-- Unsaved Changes Confirmation Modal -->
    <transition name="modal">
      <div v-if="showUnsavedModal" class="confirm-modal-mask">
        <div class="confirm-modal-wrapper" @click="cancelLeave">
          <div class="confirm-modal-container" @click.stop>
            <!-- Header -->
            <div class="confirm-header d-flex justify-content-between align-items-center">
              <h6 class="confirm-title mb-0">Perubahan Belum Disimpan</h6>
              <button
                type="button"
                class="btn btn-close-confirm"
                @click="cancelLeave"
              >
                <i class="fas fa-times"></i>
              </button>
            </div>

            <!-- Body Content -->
            <div class="confirm-body text-center">
              <div class="confirm-icon-wrapper">
                <svg
                  width="108"
                  height="98"
                  viewBox="0 0 124 112"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M53.493 2.18918C61.9236 -2.48607 72.6478 0.58571 77.359 8.94096L122.425 87.1547C123.417 89.4893 123.851 91.3877 123.975 93.3597C124.223 97.9674 122.611 102.446 119.45 105.893C116.288 109.327 112.011 111.354 107.362 111.6H16.6092C14.6875 111.483 12.7659 111.047 10.9682 110.371C1.97967 106.747 -2.3596 96.5482 1.29778 87.7077L46.6741 8.88567C48.2239 6.11492 50.5795 3.72507 53.493 2.18918ZM61.9856 76.0902C59.0101 76.0902 56.5305 78.5476 56.5305 81.5027C56.5305 84.4454 59.0101 86.909 61.9856 86.909C64.9611 86.909 67.3786 84.4454 67.3786 81.4351C67.3786 78.4923 64.9611 76.0902 61.9856 76.0902ZM61.9856 37.7604C59.0101 37.7604 56.5305 40.1503 56.5305 43.1054V60.4855C56.5305 63.4344 59.0101 65.898 61.9856 65.898C64.9611 65.898 67.3786 63.4344 67.3786 60.4855V43.1054C67.3786 40.1503 64.9611 37.7604 61.9856 37.7604Z"
                    fill="#F36F21"
                  />
                </svg>
              </div>

              <div class="confirm-text">
                <p class="unsaved-text-msg mb-2">
                  Perubahan pada halaman ini belum disimpan dan akan hilang jika Anda keluar.
                </p>
                <p class="unsaved-text-confirm mb-0">
                  Yakin ingin keluar tanpa menyimpan?
                </p>
              </div>
            </div>

            <!-- Actions -->
            <div class="confirm-actions d-flex justify-content-center align-items-center">
              <button
                type="button"
                class="btn btn-confirm-batal"
                @click="cancelLeave"
              >
                Batal
              </button>
              <button
                type="button"
                class="btn btn-confirm-keluar"
                @click="confirmLeave"
              >
                Ya, Keluar
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { fetchTransferQtyOptions } from "@/services/transfer-qty-options";

export default {
  name: "TransferAmountSetting",
  metaInfo: {
    title: "Atur Jumlah Transfer QR",
  },
  data() {
    return {
      isPreviewOpen: true,
      isSaving: false,
      showSuccessToast: false,
      showDeleteToast: false,
      deletedOptionValue: "",
      toastTimeout: null,
      showUnsavedModal: false,
      // { kind: "route", next } | { kind: "discard" }
      pendingLeave: null,
      forceLeave: false,
      initialOptions: [],
      options: [],
    };
  },
  computed: {
    validOptions() {
      return this.options.filter(
        (opt) => opt.value !== null && opt.value !== undefined && opt.value !== ""
      );
    },
    isChanged() {
      const currentValues = this.options.map((opt) => opt.value);
      const initialValues = this.initialOptions.map((opt) => opt.value);
      return JSON.stringify(currentValues) !== JSON.stringify(initialValues);
    },
    isUserSE() {
      return this.$store.state.userData.userData.role === 'USER SE';
    },
  },
  beforeMount() {
    const el = document.getElementById("content");
    if (el) el.style.background = "#F8F8F8";
    window.addEventListener("beforeunload", this.handleBeforeUnload);
  },
  beforeDestroy() {
    const el = document.getElementById("content");
    if (el) el.style.background = "";
    window.removeEventListener("beforeunload", this.handleBeforeUnload);
    if (this.toastTimeout) {
      clearTimeout(this.toastTimeout);
    }
  },
  mounted() {
    this.fetchSettings();
  },
  beforeRouteLeave(to, from, next) {
    if (this.canLeaveRoute(to, next)) {
      next();
    }
  },
  methods: {
    canLeaveRoute(to, nextCallback) {
      return this.requestLeave({ kind: "route", next: nextCallback });
    },
    requestLeave(intent) {
      if (this.isChanged && !this.forceLeave) {
        this.pendingLeave = intent;
        this.showUnsavedModal = true;
        return false;
      }
      return true;
    },
    cancelLeave() {
      this.showUnsavedModal = false;
      this.pendingLeave = null;
    },
    confirmLeave() {
      const intent = this.pendingLeave;
      this.showUnsavedModal = false;
      this.pendingLeave = null;
      if (intent) this.applyLeave(intent);
    },
    applyLeave(intent) {
      if (intent.kind === "route") {
        this.forceLeave = true;
        intent.next();
        return;
      }
      this.options = JSON.parse(JSON.stringify(this.initialOptions));
      this.leavePage();
    },
    leavePage() {
      if (window.history.length > 1) {
        this.$router.back();
      } else {
        this.$router.push({ name: "Welcome" }).catch(() => {});
      }
    },
    handleBeforeUnload(e) {
      if (this.isChanged && !this.forceLeave) {
        e.preventDefault();
        e.returnValue = "";
      }
    },
    triggerSuccessToast() {
      if (this.toastTimeout) {
        clearTimeout(this.toastTimeout);
      }
      this.showDeleteToast = false;
      this.showSuccessToast = true;
      this.toastTimeout = setTimeout(() => {
        this.showSuccessToast = false;
      }, 4000);
    },
    triggerDeleteToast(val) {
      if (this.toastTimeout) {
        clearTimeout(this.toastTimeout);
      }
      this.showSuccessToast = false;
      this.deletedOptionValue = val || "";
      this.showDeleteToast = true;
      this.toastTimeout = setTimeout(() => {
        this.showDeleteToast = false;
      }, 4000);
    },
    async fetchSettings() {
      try {
        const list = await fetchTransferQtyOptions(this.$http);
        this.options = list.map((value) => ({ value }));
        this.initialOptions = list.map((value) => ({ value }));
      } catch (err) {
        console.error("Gagal memuat opsi transfer QR:", err);
        this.$swal.fire({
          title: "Gagal Memuat Opsi",
          text: (err && err.message) || "Terjadi kesalahan saat memuat opsi transfer QR",
          icon: "error",
          confirmButtonColor: "#F36F21",
        });
      }
    },
    addOption() {
      this.options.push({ value: "" });
      this.$nextTick(() => {
        const inputs = this.$refs.optionInputs || [];
        const lastInput = inputs[inputs.length - 1];
        if (lastInput) lastInput.focus();
      });
    },
    handleOptionInput(index, event) {
      const digits = event.target.value.replace(/\D/g, "");
      const value = digits ? Number(digits) : "";
      event.target.value = String(value);
      this.options[index].value = value;
    },
    removeOption(index) {
      if (this.options.length <= 1) {
        this.$swal.fire({
          title: "Peringatan",
          text: "Minimal harus ada 1 opsi jumlah transfer QR",
          icon: "warning",
          confirmButtonColor: "#F36F21",
        });
        return;
      }
      const deletedVal = this.options[index].value;
      this.options.splice(index, 1);
      this.triggerDeleteToast(deletedVal);
    },
    handleCancel() {
      const intent = { kind: "discard" };
      if (this.requestLeave(intent)) this.applyLeave(intent);
    },
    async handleSave() {
      const hasEmpty = this.options.some(
        (opt) => opt.value === "" || opt.value === null || isNaN(Number(opt.value)) || Number(opt.value) <= 0
      );
      if (hasEmpty) {
        this.$swal.fire({
          title: "Input Tidak Valid",
          text: "Pastikan seluruh nilai jumlah transfer QR telah diisi angka lebih dari 0",
          icon: "error",
          confirmButtonColor: "#F36F21",
        });
        return;
      }

      this.isSaving = true;
      try {
        const payload = {
          options: this.options.map((opt) => Number(opt.value)),
        };
        await this.$http.put("/api/qrs/transfer-qty-options", payload);

        this.initialOptions = JSON.parse(JSON.stringify(this.options));
        this.triggerSuccessToast();
      } catch (err) {
        this.$swal.fire({
          title: "Gagal Menyimpan",
          text: (err.response && err.response.data && err.response.data.message) || "Terjadi kesalahan saat menyimpan opsi transfer QR",
          icon: "error",
          confirmButtonColor: "#F36F21",
        });
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<style scoped>
.transfer-amount-page {
  font-family: inherit;
}

.page-title {
  font-size: 18px;
  font-weight: 600;
  color: #2F2F2F;
  line-height: 24px;
}

.setting-card {
  background: #FFFFFF;
  border-radius: 8px;
  border: 1px solid #ECECEC;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  padding: 28px 32px 24px 32px;
}

.card-header-section {
  border-bottom: 1px solid #F3F4F6;
  padding-bottom: 18px;
  margin-bottom: 24px;
}

.card-main-title {
  font-size: 16px;
  font-weight: 600;
  line-height: normal;
  color: #2F2F2F;
  margin-bottom: 4px;
}

.card-description {
  margin: 0;
  font-size: 12px;
  line-height: normal;
  color: #707070;
}

.form-column {
  max-width: 540px;
}

.field-label-desc {
  font-size: 12px;
  color: #8C8C8C;
  font-weight: 400;
  display: block;
  margin-bottom: 4px;
}

.field-name-title {
  font-size: 14px;
  font-weight: 600;
  color: #2F2F2F;
  margin-bottom: 20px;
}

.option-row {
  margin-bottom: 16px;
}

.row-index-number {
  font-size: 13px;
  color: #8C8C8C;
  width: 24px;
  flex-shrink: 0;
}

.input-suffix-wrapper {
  position: relative;
  max-width: 460px;
}

.custom-qty-input {
  height: 38px;
  border: 1px solid #E5E7EB;
  border-radius: 4px;
  font-size: 13px;
  color: #2F2F2F;
  padding: 6px 44px 6px 14px;
  box-shadow: none;
  background-color: #FFFFFF;
  transition: border-color 0.2s;
}

.custom-qty-input:focus {
  border-color: #F36F21;
  box-shadow: none;
}

.custom-qty-input::placeholder {
  color: #ADADAD;
}

.input-suffix-text {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 13px;
  color: #9CA3AF;
  pointer-events: none;
}

.btn-delete-row {
  background: transparent;
  border: none;
  color: #ADADAD;
  font-size: 14px;
  cursor: pointer;
  padding: 6px 10px;
  margin-left: 8px;
  transition: color 0.15s;
  line-height: 1;
}

.btn-delete-row:hover {
  color: #EF4444;
}

.btn-add-option {
  background: transparent;
  border: none;
  color: #F36F21;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  padding: 0;
  margin-top: 8px;
  transition: color 0.15s;
}

.btn-add-option:hover {
  color: #D95D16;
  text-decoration: none;
}

.btn-add-option i {
  font-size: 12px;
}

/* Right: Preview Column */
.preview-column {
  flex-shrink: 0;
  margin-left: 24px;
}

.preview-card-box {
  background: #F9FAFB;
  border: 1px solid #F3F4F6;
  border-radius: 8px;
  padding: 16px 20px;
  width: 320px;
}

.preview-header-label {
  font-size: 12px;
  color: #6B7280;
  font-weight: 500;
  margin-bottom: 12px;
}

.preview-field-title {
  font-size: 13px;
  font-weight: 600;
  color: #1F2937;
  margin-bottom: 8px;
}

.preview-select-trigger {
  background: #FFFFFF;
  border: 1px solid #F36F21;
  border-radius: 4px;
  height: 38px;
  padding: 6px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  user-select: none;
}

.preview-select-placeholder {
  font-size: 13px;
  color: #2F2F2F;
}

.preview-caret-icon {
  font-size: 12px;
  color: #2F2F2F;
}

.preview-dropdown-menu {
  background: #FFFFFF;
  border: 1px solid #E5E7EB;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  margin-top: 4px;
  overflow: hidden;
}

.preview-dropdown-item {
  padding: 9px 12px;
  font-size: 13px;
  color: #2F2F2F;
  border-bottom: 1px solid #F9FAFB;
  cursor: default;
}

.preview-dropdown-item:last-child {
  border-bottom: none;
}

.preview-dropdown-empty {
  padding: 10px 12px;
  font-size: 12px;
  text-align: center;
}

/* Card Actions */
.card-actions-section {
  margin-top: 48px;
  padding-top: 16px;
}

.btn-action-batal {
  background: transparent;
  border: none;
  color: #F36F21;
  font-size: 14px;
  font-weight: 500;
  padding: 8px 16px;
  cursor: pointer;
  line-height: 1;
}

.btn-action-batal:hover {
  color: #D95D16;
}

.btn-action-simpan {
  height: 36px;
  min-width: 90px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  border: none;
  padding: 0 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: #CBD5E1;
  color: #FFFFFF;
  cursor: not-allowed;
  transition: background-color 0.2s;
  line-height: 1;
}

.btn-action-simpan--active {
  background-color: #F36F21 !important;
  color: #FFFFFF !important;
  cursor: pointer !important;
}

.btn-action-simpan--active:hover {
  background-color: #D95D16 !important;
}

/* Top Right Success / Delete Toast */
.top-right-toast {
  position: fixed;
  top: 72px;
  right: 24px;
  z-index: 2000;
  width: 360px;
  height: 60px;
  min-height: 60px;
  background-color: #FFFFFF;
  border: 1px solid #E5E7EB;
  border-width: 1px;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  gap: 12px;
  box-sizing: border-box;
  transform: rotate(0deg);
  opacity: 1;
}

.toast-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.toast-icon-wrapper svg {
  display: block;
}

.toast-content-wrapper {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.toast-title {
  font-size: 13px;
  font-weight: 600;
  color: #1F2937;
  line-height: 1.3;
  margin-bottom: 2px;
}

.toast-desc {
  font-size: 12px;
  color: #6B7280;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.toast-close-btn {
  background: transparent;
  border: none;
  padding: 2px;
  color: #9CA3AF;
  font-size: 13px;
  cursor: pointer;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  outline: none;
  box-shadow: none;
  flex-shrink: 0;
  transition: opacity 0.15s;
}

.toast-close-btn:hover {
  opacity: 0.7;
}

.toast-slide-enter-active,
.toast-slide-leave-active {
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

.toast-slide-enter {
  opacity: 0;
  transform: translateY(-20px) scale(0.96);
}

.toast-slide-leave-to {
  opacity: 0;
  transform: translateY(-10px) scale(0.96);
}

/* Unsaved Changes Confirmation Modal Styles */
.confirm-modal-mask {
  position: fixed;
  z-index: 1060;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.3s ease;
  padding: 20px;
}

.confirm-modal-wrapper {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.confirm-modal-container {
  width: 401px;
  max-width: 401px;
  height: 330px;
  background-color: #FFFFFF;
  border-radius: 8px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.2);
  padding: 16px 20px 20px 20px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow: hidden;
  box-sizing: border-box;
}

.confirm-header {
  width: 100%;
}

.confirm-title {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
}

.btn-close-confirm {
  padding: 0;
  border: none;
  background: transparent;
  color: #111827;
  font-size: 16px;
  cursor: pointer;
  line-height: 1;
  outline: none;
  box-shadow: none;
}

.btn-close-confirm:hover {
  color: #6B7280;
}

.confirm-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: auto 0;
}

.confirm-icon-wrapper {
  margin-bottom: 16px;
}

.confirm-icon-wrapper svg {
  display: block;
}

.confirm-text {
  max-width: 320px;
}

.unsaved-text-msg {
  font-size: 13px;
  color: #1F2937;
  line-height: 1.5;
  text-align: center;
}

.unsaved-text-confirm {
  font-size: 13px;
  color: #1F2937;
  line-height: 1.5;
  text-align: center;
}

.confirm-actions {
  margin-top: 8px;
}

.btn-confirm-batal {
  background: transparent;
  border: none;
  color: #F36F21;
  font-size: 14px;
  font-weight: 500;
  padding: 8px 16px;
  margin-right: 20px;
  cursor: pointer;
  box-shadow: none;
  outline: none;
  line-height: 1;
}

.btn-confirm-batal:hover {
  color: #D95D16;
}

.btn-confirm-keluar {
  background-color: #F36F21;
  border: 1px solid #F36F21;
  color: #FFFFFF;
  font-size: 14px;
  font-weight: 500;
  border-radius: 4px;
  padding: 8px 24px;
  min-width: 130px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: none;
  cursor: pointer;
  outline: none;
  line-height: 1;
  transition: background-color 0.2s, border-color 0.2s;
}

.btn-confirm-keluar:hover {
  background-color: #D95D16;
  border-color: #D95D16;
  color: #FFFFFF;
}

.modal-enter {
  opacity: 0;
}

.modal-leave-active {
  opacity: 0;
}

.modal-enter .confirm-modal-container,
.modal-leave-active .confirm-modal-container {
  -webkit-transform: scale(1.05);
  transform: scale(1.05);
}
.custom-qty-input:disabled {
  background-color: #F9FAFB;
  color: #2F2F2F;
  cursor: not-allowed;
}
</style>
